<template>
  <div class="conduct-form">
    <div class="form-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary hover-white" @click="$router.back()" title="Back">
            <i class="fas fa-arrow-left hover-white"></i>
          </button>
          <div>
            <h3 class="mb-0 fw-bold">{{ workOrder.name || "Untitled Form" }}</h3>
            <small class="text-muted">#{{ workOrder.external_id || "N/A" }}</small>
          </div>
        </div>
        <div class="d-flex align-items-center gap-3">
          <span v-html="getStatusBadge(submitResult.status || '')"></span>
          <button type="button" class="btn btn-sm btn-outline-secondary toggle-details-btn" @click="toggleDetails"
            title="Toggle details">
            <i class="fas fa-chevron-down" :class="{ 'rotated': detailsExpanded }"></i>
            <span class="ms-1">{{ detailsExpanded ? 'Hide' : 'Show' }} Details</span>
          </button>
        </div>
      </div>

      <!-- Details Section -->
      <div v-show="detailsExpanded" class="details-section">
        <hr class="my-2">
        <div class="row g-2">
          <!-- Priority -->
          <div class="col-md-3 col-sm-6">
            <div class="detail-item">
              <span class="detail-label">Priority</span>
              <span class="detail-value">
                <span class="priority-badge" :class="getPriorityClass(workOrder.priority)">
                  {{ workOrder.priority || 'N/A' }}
                </span>
              </span>
            </div>
          </div>

          <!-- Work Type -->
          <div class="col-md-3 col-sm-6">
            <div class="detail-item">
              <span class="detail-label">Work Type</span>
              <span class="detail-value">{{ workOrder.work_type || 'N/A' }}</span>
            </div>
          </div>

          <!-- Location -->
          <div class="col-md-3 col-sm-6">
            <div class="detail-item">
              <span class="detail-label">Location</span>
              <span class="detail-value">{{ workOrder.get_location?.name || 'N/A' }}</span>
            </div>
          </div>

          <!-- Description -->
          <div class="col-md-3 col-sm-6">
            <div class="detail-item">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ workOrder.description || 'N/A' }}</span>
            </div>
          </div>

          <!-- Requests -->
          <div class="col-md-6">
            <div class="detail-item">
              <span class="detail-label">Requests</span>
              <div class="detail-value">
                <span v-if="workOrder.get_requests && workOrder.get_requests.length > 0">
                  <span v-for="(request, index) in workOrder.get_requests" :key="request.id" class="badge-tag">
                    {{ request.title }}
                  </span>
                </span>
                <span v-else>N/A</span>
              </div>
            </div>
          </div>

          <!-- Categories -->
          <div class="col-md-6">
            <div class="detail-item">
              <span class="detail-label">Categories</span>
              <div class="detail-value">
                <span v-if="workOrder.get_categories && workOrder.get_categories.length > 0">
                  <span v-for="(category, index) in workOrder.get_categories" :key="category.id" class="badge-tag">
                    {{ category.name }}
                  </span>
                </span>
                <span v-else>N/A</span>
              </div>
            </div>
          </div>

          <!-- Assets -->
          <div class="col-md-12">
            <div class="detail-item">
              <span class="detail-label">Assets</span>
              <div class="detail-value">
                <span v-if="workOrder.get_assets && workOrder.get_assets.length > 0">
                  <span v-for="(asset, index) in workOrder.get_assets" :key="asset.id" class="badge-tag">
                    {{ asset.name }}
                  </span>
                </span>
                <span v-else>N/A</span>
              </div>
            </div>
          </div>

          <!-- Attachments -->
          <div class="col-md-12" v-if="workOrder.get_attachments && workOrder.get_attachments.length > 0">
            <div class="detail-item">
              <span class="detail-label">Attachments</span>
              <div class="detail-value attachments-list">
                <div v-for="(file, index) in workOrder.get_attachments" :key="index" class="attachment-item">
                  <i class="fas fa-paperclip"></i>
                  <a :href="getFilePath(file.file_path)" target="_blank" class="attachment-link">
                    {{ file.file_name }}
                  </a>
                  <!-- Show image thumbnail -->
                  <span v-if="isImageFile(file.file_name)" class="image-thumbnail"
                    @click="previewImage(file.file_path)">
                    <img :src="getFilePath(file.file_path)" :alt="file.file_name" class="thumbnail-img" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Work Order Details Section -->


    <div v-if="showUnauthorized" class="unauthorized-container">
      <div class="unauthorized-card">
        <div class="unauthorized-icon">
          <i class="fas fa-lock"></i>
        </div>
        <h3 class="unauthorized-title">Access Denied</h3>
        <p class="unauthorized-message">
          {{ authMessage || "You are not authorized to view this schedule." }}
        </p>
        <button class="btn btn-primary mt-3" @click="$router.back()">
          <i class="fas fa-arrow-left"></i> Go Back
        </button>
      </div>
    </div>

    <div v-else-if="isSubmitted" class="success-container">
      <div class="success-card">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h3 class="success-title">Submission Successful!</h3>
        <p class="success-message">
          Your responses have been submitted successfully.
        </p>

        <div class="status-message" v-if="submitResult && submitResult.status">
          <div v-if="submitResult.status === 'under_action'" class="status-box status-action">
            <i class="fas fa-tools"></i>
            <span>sent to action takers as it requires
              <strong>Actions</strong>.</span>
          </div>
          <div v-else-if="submitResult.status === 'under_verification'" class="status-box status-verification">
            <i class="fas fa-check-circle"></i>
            <span>Sent to verifier to perform <strong>Verification</strong>.</span>
          </div>
          <div v-else-if="submitResult.status === 'completed'" class="status-box status-completed">
            <i class="fas fa-check-double"></i>
            <span>This inspection has been
              <strong>Completed</strong> successfully!</span>
          </div>
          <div v-else class="status-box status-default">
            <i class="fas fa-info-circle"></i>
            <span>Status:
              <strong>{{ submitResult.status || "Submitted" }}</strong></span>
          </div>
        </div>
      </div>
    </div>

    <div v-else id="conduct_form">
      <LoadingBar v-if="loading" message="Loading templates..." />
      <div v-else>
        <div v-for="(item, index) in template.get_content" :key="item.id" class="form-item">
          <!-- Heading -->
          <div v-if="item.type === 'heading'" class="heading-block">
            <h4 class="heading-title">{{ item.title || "Heading" }}</h4>
          </div>

          <!-- Section -->
          <div v-else-if="item.type === 'section'" class="section-block">
            <div class="section-header">
              <i class="fas fa-layer-group section-icon"></i>
              <h4 class="section-title">{{ item.title || "Section" }}</h4>
            </div>

            <div class="sub-questions">
              <div v-for="(question, qIndex) in item.get_content" :key="question.id" class="question-block sub-question"
                :class="{
                  'has-correction': getCorrection(question),
                  'has-corrective-action': getCorrectiveAction(question),
                  'has-error': hasQuestionError(question),
                }" :data-question-id="question.id">
                <div class="question-header">
                  <span class="question-number">{{ index + 1 }}.{{ qIndex + 1 }}</span>
                  <span class="question-text">{{
                    question.title || "Untitled Question"
                  }}</span>
                  <span v-if="question.required" class="required-badge">*</span>
                  <span v-if="question.multi_select" class="multi-badge">Multi-Select</span>
                </div>

                <!-- Show correction/corrective action messages -->
                <div v-if="getCorrection(question)" class="logic-message correction">
                  <i class="fas fa-tools"></i> Correction is required
                </div>
                <div v-if="getCorrectiveAction(question)" class="logic-message corrective-action">
                  <i class="fas fa-exclamation-triangle"></i> Corrective action
                  is required
                </div>
                <div v-if="hasQuestionError(question)" class="logic-message corrective-action">
                  <i class="fas fa-exclamation-circle"></i>
                  Mandatory Question
                </div>

                <!-- Render question based on response type -->
                <div class="question-render">
                  <!-- 1. TEXT -->
                  <template v-if="
                    question.get_response &&
                    question.get_response.type === 'text'
                  ">
                    <div class="text-input-wrapper">
                      <textarea class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" placeholder="Enter your answer..." rows="3"
                        :readonly="isReadOnly"></textarea>
                    </div>
                  </template>

                  <!-- 2. NUMBER -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'number'
                  ">
                    <div class="number-input-wrapper">
                      <input type="number" class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" placeholder="Enter a number..."
                        :readonly="isReadOnly" />
                    </div>
                  </template>

                  <!-- 3. DATE-TIME -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'datetime'
                  ">
                    <div class="datetime-input-wrapper">
                      <input type="datetime-local" class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" :readonly="isReadOnly" />
                    </div>
                  </template>

                  <!-- 4. DATE -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'date'
                  ">
                    <div class="date-input-wrapper">
                      <input type="date" class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" :readonly="isReadOnly" />
                    </div>
                  </template>

                  <!-- 5. TIME -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'time'
                  ">
                    <div class="time-input-wrapper">
                      <input type="time" class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" :readonly="isReadOnly" />
                    </div>
                  </template>

                  <!-- 6. PICTURE -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'picture'
                  ">
                    <div class="file-input-wrapper">
                      <input type="file" class="form-control form-control-thin file-input"
                        @change="handleFileUpload(question.id, $event)" accept="image/*" :disabled="isReadOnly" />
                      <small class="text-muted">Upload an image (JPG, PNG, GIF)</small>
                      <div v-if="fileNames[question.id]" class="file-name">
                        <i class="fas fa-check-circle" style="color: #10b981"></i>
                        {{ fileNames[question.id] }}
                      </div>
                    </div>
                  </template>

                  <!-- 7. FILE -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'file'
                  ">
                    <div class="file-input-wrapper">
                      <input type="file" class="form-control form-control-thin file-input"
                        @change="handleFileUpload(question.id, $event)" accept="*/*" :disabled="isReadOnly" />
                      <small class="text-muted">Upload a file (PDF, DOC, etc.)</small>
                      <div v-if="fileNames[question.id]" class="file-name">
                        <i class="fas fa-check-circle" style="color: #10b981"></i>
                        {{ fileNames[question.id] }}
                      </div>
                    </div>
                  </template>

                  <!-- 8. RANGE -->
                  <template v-else-if="
                    question.get_response &&
                    question.get_response.type === 'range'
                  ">
                    <div class="range-input-wrapper">
                      <div class="range-header">
                        <span class="range-value">{{
                          getAnswer(question) || question.get_response.min || 0
                        }}</span>
                        <span class="range-label">{{
                          question.get_response.max || 100
                        }}</span>
                      </div>
                      <input type="range" class="form-range" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" :min="question.get_response.min || 0"
                        :max="question.get_response.max || 100" :step="question.get_response.step || 1"
                        :disabled="isReadOnly" />
                      <div class="range-labels">
                        <span class="range-min">{{
                          question.get_response.min_label || "Min"
                        }}</span>
                        <span class="range-max">{{
                          question.get_response.max_label || "Max"
                        }}</span>
                      </div>
                    </div>
                  </template>

                  <!-- 9. MULTIPLE CHOICE -->
                  <template v-else-if="
                    question.get_response && question.get_response.get_options
                  ">
                    <div class="options-grid">
                      <div v-for="option in question.get_response.get_options" :key="option.id" class="option-box"
                        :class="{ selected: isSelected(question, option) }" :style="{
                          borderColor: isSelected(question, option)
                            ? option.bg || '#667eea'
                            : '#e0e0e0',
                          backgroundColor: isSelected(question, option)
                            ? (option.bg || '#667eea') + '15'
                            : 'transparent',
                        }" @click="!isReadOnly && selectOption(question, option)">
                        <div class="option-color" :style="{ backgroundColor: option.bg || '#667eea' }"></div>
                        <span class="option-name">{{ option.name }}</span>
                        <div class="option-check">
                          <i v-if="isSelected(question, option)" class="fas fa-check-circle"
                            :style="{ color: option.bg || '#667eea' }"></i>
                        </div>
                      </div>
                    </div>
                    <small v-if="question.multi_select" class="text-muted">Select multiple options</small>
                  </template>

                  <!-- 10. DEFAULT -->
                  <template v-else>
                    <div class="text-input-wrapper">
                      <input type="text" class="form-control form-control-thin" :value="getAnswer(question)"
                        @input="updateAnswer(question, $event.target.value)" placeholder="Enter your answer..."
                        :readonly="isReadOnly" />
                    </div>
                  </template>
                </div>

                <!-- Attachment Section -->
                <AttachmentList :files="getAttachments(question)" :readonly="isReadOnly"
                  :uploading="uploadingFiles[question.id]" @select="
                    (files) => handleAttachmentSelect(question.id, files)
                  " @remove="(idx) => removeAttachment(question.id, idx)" @update-note="
                    (data) =>
                      updateAttachmentNote(question.id, data.fileId, data.note)
                  " />

                <!-- Note textarea for note trigger -->
                <div v-if="getNoteTrigger(question)" class="note-textarea-wrapper">
                  <textarea class="form-control form-control-thin" :value="getNoteValue(question)"
                    @input="updateNote(question, $event.target.value)" placeholder="Enter your note..." rows="2"
                    :readonly="isReadOnly"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Standalone Question -->
          <div v-else-if="item.type === 'question'" class="question-block" :class="{
            'has-correction': getCorrection(item),
            'has-corrective-action': getCorrectiveAction(item),
            'has-error': hasQuestionError(item),
          }" :data-question-id="item.id">
            <div class="question-header">
              <span class="question-number">{{ index + 1 }}</span>
              <span class="question-text">{{
                item.title || "Untitled Question"
              }}</span>
              <span v-if="item.required" class="required-badge">*</span>
              <span v-if="item.multi_select" class="multi-badge">Multi-Select</span>
            </div>

            <!-- Show correction/corrective action messages -->
            <div v-if="getCorrection(item)" class="logic-message correction">
              <i class="fas fa-exclamation-triangle"></i> Correction is required
            </div>
            <div v-if="getCorrectiveAction(item)" class="logic-message corrective-action">
              <i class="fas fa-tools"></i> Corrective action is required
            </div>
            <div v-if="hasQuestionError(item)" class="logic-message corrective-action">
              <i class="fas fa-exclamation-circle"></i>
              Mandatory Question
            </div>

            <div class="question-render">
              <!-- 1. TEXT -->
              <template v-if="item.get_response && item.get_response.type === 'text'">
                <div class="text-input-wrapper">
                  <textarea class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" placeholder="Enter your answer..." rows="3"
                    :readonly="isReadOnly"></textarea>
                </div>
              </template>

              <!-- 2. NUMBER -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'number'
              ">
                <div class="number-input-wrapper">
                  <input type="number" class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" placeholder="Enter a number..."
                    :readonly="isReadOnly" />
                </div>
              </template>

              <!-- 3. DATE-TIME -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'datetime'
              ">
                <div class="datetime-input-wrapper">
                  <input type="datetime-local" class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" :readonly="isReadOnly" />
                </div>
              </template>

              <!-- 4. DATE -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'date'
              ">
                <div class="date-input-wrapper">
                  <input type="date" class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" :readonly="isReadOnly" />
                </div>
              </template>

              <!-- 5. TIME -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'time'
              ">
                <div class="time-input-wrapper">
                  <input type="time" class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" :readonly="isReadOnly" />
                </div>
              </template>

              <!-- 6. PICTURE -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'picture'
              ">
                <div class="file-input-wrapper">
                  <input type="file" class="form-control form-control-thin file-input"
                    @change="handleFileUpload(item.id, $event)" accept="image/*" :disabled="isReadOnly" />
                  <small class="text-muted">Upload an image (JPG, PNG, GIF)</small>
                  <div v-if="fileNames[item.id]" class="file-name">
                    <i class="fas fa-check-circle" style="color: #10b981"></i>
                    {{ fileNames[item.id] }}
                  </div>
                </div>
              </template>

              <!-- 7. FILE -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'file'
              ">
                <div class="file-input-wrapper">
                  <input type="file" class="form-control form-control-thin file-input"
                    @change="handleFileUpload(item.id, $event)" accept="*/*" :disabled="isReadOnly" />
                  <small class="text-muted">Upload a file (PDF, DOC, etc.)</small>
                  <div v-if="fileNames[item.id]" class="file-name">
                    <i class="fas fa-check-circle" style="color: #10b981"></i>
                    {{ fileNames[item.id] }}
                  </div>
                </div>
              </template>

              <!-- 8. RANGE -->
              <template v-else-if="
                item.get_response && item.get_response.type === 'range'
              ">
                <div class="range-input-wrapper">
                  <div class="range-header">
                    <span class="range-value">{{
                      getAnswer(item) || item.get_response.min || 0
                    }}</span>
                    <span class="range-label">{{
                      item.get_response.max || 100
                    }}</span>
                  </div>
                  <input type="range" class="form-range" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" :min="item.get_response.min || 0"
                    :max="item.get_response.max || 100" :step="item.get_response.step || 1" :disabled="isReadOnly" />
                  <div class="range-labels">
                    <span class="range-min">{{
                      item.get_response.min_label || "Min"
                    }}</span>
                    <span class="range-max">{{
                      item.get_response.max_label || "Max"
                    }}</span>
                  </div>
                </div>
              </template>

              <!-- 9. MULTIPLE CHOICE -->
              <template v-else-if="item.get_response && item.get_response.get_options">
                <div class="options-grid">
                  <div v-for="option in item.get_response.get_options" :key="option.id" class="option-box"
                    :class="{ selected: isSelected(item, option) }" :style="{
                      borderColor: isSelected(item, option)
                        ? option.bg || '#667eea'
                        : '#e0e0e0',
                      backgroundColor: isSelected(item, option)
                        ? (option.bg || '#667eea') + '15'
                        : 'transparent',
                    }" @click="!isReadOnly && selectOption(item, option)">
                    <div class="option-color" :style="{ backgroundColor: option.bg || '#667eea' }"></div>
                    <span class="option-name">{{ option.name }}</span>
                    <div class="option-check">
                      <i v-if="isSelected(item, option)" class="fas fa-check-circle"
                        :style="{ color: option.bg || '#667eea' }"></i>
                    </div>
                  </div>
                </div>
                <small v-if="item.multi_select" class="text-muted">Select multiple options</small>
              </template>

              <!-- 10. DEFAULT -->
              <template v-else>
                <div class="text-input-wrapper">
                  <input type="text" class="form-control form-control-thin" :value="getAnswer(item)"
                    @input="updateAnswer(item, $event.target.value)" placeholder="Enter your answer..."
                    :readonly="isReadOnly" />
                </div>
              </template>
            </div>

            <!-- Attachment Section -->
            <AttachmentList :files="getAttachments(item)" :readonly="isReadOnly" :uploading="uploadingFiles[item.id]"
              @select="(files) => handleAttachmentSelect(item.id, files)"
              @remove="(idx) => removeAttachment(item.id, idx)" @update-note="
                (data) => updateAttachmentNote(item.id, data.fileId, data.note)
              " />

            <!-- Note textarea for note trigger -->
            <div v-if="getNoteTrigger(item)" class="note-textarea-wrapper">
              <textarea class="form-control form-control-thin" :value="getNoteValue(item)"
                @input="updateNote(item, $event.target.value)" placeholder="Enter your note..." rows="2"
                :readonly="isReadOnly"></textarea>
            </div>
          </div>

          <!-- Table -->
          <div v-else-if="item.type === 'table'" class="table-block">
            <div class="table-header">
              <i class="fas fa-table table-icon"></i>
              <h4 class="table-title">{{ item.title || "Table" }}</h4>
            </div>
          </div>
        </div>

        <!-- Direct Actions Section (when no template id - started status) -->
        <div v-if="!template.id && schedule.status === 'started'" class="action-section">
          <div class="action-header">
            <i class="fas fa-tools"></i>
            <h4>Perform Actions</h4>
          </div>

          <div class="action-body">
            <div class="mb-3">
              <label class="form-label form-label-thin">Diagnosis/Root Causes come here<span
                  class="text-danger">*</span></label>
              <textarea class="form-control form-control-thin action-textarea" required id="root_causes"
                :readonly="isReadOnly || schedule.status != 'started'" name="root_causes" ref="root_causes"
                v-model="actions.root_cause" placeholder="Enter Root Cause..." rows="2"></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label form-label-thin">Actions Taken/Performed
                <span class="text-danger">*</span></label>
              <textarea class="form-control form-control-thin action-textarea" required id="actions"
                :readonly="isReadOnly || schedule.status != 'started'" name="actions" ref="actions"
                v-model="actions.actions" placeholder="Enter Actions Performed/Taken..." rows="2"></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label form-label-thin">Challenges Faced If Any</label>
              <textarea class="form-control form-control-thin action-textarea" id="challenges"
                :readonly="isReadOnly || schedule.status != 'started'" name="challenges" ref="challenges"
                v-model="actions.challenges" placeholder="Enter Challenges..." rows="2"></textarea>
            </div>

            <div class="form-actions mt-4" v-if="schedule.status === 'started'">
              <button type="button" @click="saveDirectActions" class="btn btn-primary form-submit-btn"
                style="padding: 15px 40px !important" :disabled="submitting">
                <i class="fas fa-paper-plane"></i>
                {{ submitting ? "Please Wait..." : "Submit Actions" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Section (when under_action status) -->
      <div v-if="
        ['under_verification', 'under_action', 'completed'].includes(
          schedule.status,
        ) &&
        template.id
      " class="action-section">
        <div class="action-header">
          <i class="fas fa-tools"></i>
          <h4>Perform Actions</h4>
        </div>

        <div class="action-body">
          <div v-if="
            schedule.corrective_actions_count > 0 ||
            schedule.corrective_actions_count > 0
          " class="mb-3">
            <label class="form-label form-label-thin">Diagnosis/Root Causes <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin action-textarea" required id="root_causes"
              :readonly="schedule.status != 'under_action'" name="root_causes" ref="root_causes"
              v-model="actions.root_cause" placeholder="Enter Root Cause..." rows="2"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label form-label-thin">Actions Taken/Performed <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin action-textarea" required id="actions"
              :readonly="schedule.status != 'under_action'" name="actions" ref="actions" v-model="actions.actions"
              placeholder="Enter Actions Performed/Taken..." rows="2"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label form-label-thin">Challenges Faced If Any</label>
            <textarea class="form-control form-control-thin action-textarea" id="challenges"
              :readonly="schedule.status != 'under_action'" name="challenges" ref="challenges"
              v-model="actions.challenges" placeholder="Enter Challenges..." rows="2"></textarea>
          </div>

          <div class="form-actions mt-4" v-if="schedule.status === 'under_action'">
            <button type="button" @click="saveActions" class="btn btn-primary form-submit-btn"
              style="padding: 15px 40px !important" :disabled="submitting">
              <i class="fas fa-paper-plane"></i>
              {{ submitting ? "Please Wait..." : "Submit Actions" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Verification Section -->
      <div v-if="['under_verification', 'completed'].includes(schedule.status)" class="action-section">
        <div class="action-header">
          <i class="fas fa-check-circle"></i>
          <h4>Verification Remarks</h4>
        </div>

        <div class="action-body">
          <div class="mb-3">
            <label class="form-label form-label-thin">Remarks <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin action-textarea" required id="remarks" name="remarks"
              :readonly="true" ref="remarks" v-model="verification.remarks" placeholder="Enter verification remarks..."
              rows="3"></textarea>
          </div>


        </div>
      </div>

      <!-- Main Submit Button -->
      <div class="form-actions">
        <div v-if="schedule.status == 'started' && template.id">
          <button type="button" @click="openConfirmModal" class="btn btn-primary form-submit-btn"
            style="padding: 15px 40px !important" :disabled="submitting">
            <i class="fas fa-paper-plane"></i>
            {{ submitting ? "Please Wait..." : "Submit" }}
          </button>
        </div>
      </div>
    </div>
    <hr />

    <!-- Materials Section -->
    <div class="materials-section mt-4">
      <div class="materials-header">
        <div class="materials-header-left">
          <i class="fas fa-cubes"></i>
          <h4>Materials Used</h4>
        </div>
        <button type="button" title="Click to Edit" @click="openMaterialsModal" class="btn p-0 px-0 py-0"
          :disabled="isSubmitted">
          <i class="fas fa-pen"></i>
        </button>
      </div>

      <!-- Materials List -->
      <div class="materials-list" v-if="materialsUsed && materialsUsed.length > 0">
        <div v-for="material in materialsUsed" :key="material.id" class="material-item">
          <div class="material-info">
            <span class="material-name">{{
              material.get_material?.name || "Unknown Material"
            }}</span>
            <span class="material-quantity">
              {{ material.quantity }} {{ material.get_material?.unit || "" }}
            </span>
          </div>
        </div>
      </div>
      <div v-else class="no-materials">
        <p class="text-muted">No materials added yet.</p>
      </div>
    </div>

    <!-- Materials Edit Modal -->
    <FormModal ref="materialsModal" title="Manage Materials" id="materials_modal" size="lg">
      <div class="materials-modal-body">
        <div class="mb-3">
          <button type="button" @click="addMaterialRow" class="btn btn-success btn-sm">
            <i class="fas fa-plus"></i> Add Material
          </button>
        </div>

        <div v-for="(row, index) in materialRows" :key="index" class="material-row">
          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="form-label form-label-thin">Material</label>
              <select class="form-select form-control-thin" v-model="row.material_id">
                <option value="">Select Material</option>
                <option v-for="material in materialsDropdown" :key="material.id" :value="material.id">
                  {{ material.get_category?.name || "" }} - {{ material.code || "N/A" }} | {{ material.name }} (
                  {{material.quantity}} {{  material.unit || "N/A" }} )
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label form-label-thin">Quantity</label>
              <input type="number" class="form-control form-control-thin" v-model="row.quantity" min="0.01" step="0.01"
                placeholder="Enter quantity" />
            </div>
            <div class="col-md-3 d-flex align-items-center justify-content-end">
              <button type="button" @click="removeMaterialRow(index)" class="btn btn-remove-row">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="materialRows.length === 0" class="text-center text-muted py-3">
          No materials added. Click "Add Material" to add.
        </div>
      </div>

      <div class="modal-footer-actions">
        <button type="button" class="btn btn-outline-secondary px-4" @click="closeMaterialsModal">
          Cancel
        </button>
        <button type="button" class="btn btn-primary px-4" @click="saveMaterials" :disabled="submitting">
          <i class="fas fa-save me-1"></i>
          {{ submitting ? "Saving..." : "Save Materials" }}
        </button>
      </div>
    </FormModal>

    <!-- Comments Section -->
    <div class="px-3">
      <div class="comments-section">
        <div class="comments-header">
          <i class="fas fa-comments"></i>
          <h4>Comments</h4>
          <div class="ms-auto d-flex align-items-center gap-3">
            <!-- Put On Hold Checkbox Button -->
            <div
              v-if="submitResult.status !== 'on_hold' && submitResult.status !== 'completed' && submitResult.status !== 'under_verification'"
              class="checkbox-button-wrapper">
              <label class="checkbox-button" @click="statusClicked()">
                <input type="checkbox" v-model="putOnHold" />
                <span class="checkbox-btn">
                  <i class="fas fa-pause"></i> Put On Hold
                </span>
              </label>
            </div>

            <!-- Release Checkbox Button -->
            <div v-if="submitResult.status === 'on_hold'" class="checkbox-button-wrapper">
              <label class="checkbox-button" @click="statusClicked()">
                <input type="checkbox" v-model="releaseFromHold" />
                <span class="checkbox-btn release-btn">
                  <i class="fas fa-play"></i> Release
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Comment List -->
        <div class="comments-list" v-if="comments && comments.length > 0">
          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-header">
              <div class="comment-user">
                <i class="fas fa-user-circle"></i>
                <span class="comment-username">
                  <!-- check if there is not get_creator then show Anonymous user -->
                  {{ comment.get_creator?.first_name || 'Anonymous' }} {{ comment.get_creator?.last_name || '' }}
                </span>
                <span v-if="comment.status === 'on_hold'" class="badge-hold">
                  <i class="fas fa-pause"></i> On Hold
                </span>
                <span v-if="comment.status === 'release'" class="badge-release">
                  <i class="fas fa-play"></i> Released
                </span>
              </div>
              <div class="comment-actions">
                <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
                <button v-if="comment.creator_id == userId" @click="deleteComment(comment.id)"
                  class="btn btn-sm btn-link text-danger" title="Delete comment">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="comment-body">
              <p class="comment-text">{{ comment.note }}</p>

              <!-- Comment Attachment -->
              <div v-if="comment.attachment" class="comment-attachment">
                <i class="fas fa-paperclip"></i>
                <a :href="getFilePath(comment.attachment)" target="_blank" class="attachment-link">
                  {{ getFileName(comment.attachment) }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-comments">
          <p class="text-muted">No comments yet.</p>
        </div>
        <!-- Add Comment Form -->
        <div class="comment-form" v-if="!isSubmitted">
          <div class="comment-input-wrapper">
            <textarea class="form-control form-control-thin" v-model="newComment" placeholder="Write a comment..."
              rows="2"></textarea>
          </div>

          <!-- Comment Attachment Upload (Single file) -->
          <div class="comment-attachment-upload">
            <input type="file" class="form-control form-control-thin file-input"
              @change="handleCommentFileUpload($event)" accept="*/*" />
            <small class="text-muted">Upload file (optional)</small>

            <!-- Show uploaded file -->
            <div v-if="commentFile" class="comment-file-item">
              <i class="fas fa-file"></i>
              <span>{{ commentFile.name }}</span>
              <button type="button" class="btn-close btn-sm" @click="removeCommentFile"></button>
            </div>
          </div>

          <div class="comment-actions mt-2">
            <button type="button" @click="saveComment" class="btn btn-primary btn-sm"
              :disabled="submitting || !newComment.trim()">
              <i class="fas fa-paper-plane"></i>
              {{ submitting ? 'Sending...' : 'Post Comment' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Modal -->
    <FormModal ref="confirmModal" title="Confirm Submission" id="confirm_submit_modal">
      <div class="text-center py-2">
        <div class="mb-3">
          <i class="fas fa-paper-plane text-primary" style="font-size: 42px"></i>
        </div>
        <h5 class="fw-bold mb-2">Ready to submit?</h5>
        <p class="text-muted mb-0">
          Please review your answers before submitting. Once submitted, you
          won't be able to edit this form.
        </p>
      </div>

      <div v-if="requiredErrorCount > 0" class="mt-3">
        <div class="logic-message corrective-action">
          <i class="fas fa-exclamation-circle"></i>
          <span><strong>{{ requiredErrorCount }}</strong> required question(s) are
            unanswered</span>
          <button type="button" class="btn btn-sm btn-link text-danger ms-auto p-0"
            @click="showErrorList = !showErrorList">
            <i :class="showErrorList ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
              "></i>
            {{ showErrorList ? "Hide" : "View" }}
          </button>
        </div>

        <div v-show="showErrorList" class="mt-2 border rounded-3 overflow-hidden">
          <div class="list-group list-group-flush" style="max-height: 180px; overflow-y: auto">
            <div v-for="(error, index) in requiredErrors" :key="index"
              class="list-group-item d-flex align-items-start gap-2 px-3 py-2">
              <span class="badge bg-danger rounded-pill mt-1">{{
                index + 1
              }}</span>
              <span class="text-dark small fw-semibold">{{ error.title }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Fields Section - Always shown, but required only if actions > 0 -->
      <div class="mt-3">
        <div v-if="correctionCount > 0 || correctiveActionCount > 0" class="logic-message correction mb-2">
          <i class="fas fa-tools"></i>
          <span><strong>{{ correctionCount + correctiveActionCount }}</strong> question(s) require
            <strong>Action</strong></span>
        </div>

        <div class="action-fields-modal">
          <div class="mb-3">
            <label class="form-label form-label-thin">
              Diagnosis/Root Causes
              <span v-if="correctionCount > 0 || correctiveActionCount > 0" class="text-danger">*</span>
            </label>
            <textarea class="form-control form-control-thin action-textarea"
              :required="correctionCount > 0 || correctiveActionCount > 0" id="modal_root_causes"
              name="modal_root_causes" v-model="actions.root_cause" placeholder="Enter Root Cause..."
              rows="2"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label form-label-thin">
              Actions Taken/Performed
              <span v-if="correctionCount > 0 || correctiveActionCount > 0" class="text-danger">*</span>
            </label>
            <textarea class="form-control form-control-thin action-textarea"
              :required="correctionCount > 0 || correctiveActionCount > 0" id="modal_actions" name="modal_actions"
              v-model="actions.actions" placeholder="Enter Actions Performed/Taken..." rows="2"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label form-label-thin">Challenges Faced If Any</label>
            <textarea class="form-control form-control-thin action-textarea" id="modal_challenges"
              name="modal_challenges" v-model="actions.challenges" placeholder="Enter Challenges..."
              rows="2"></textarea>
          </div>
        </div>
      </div>

      <hr class="my-3" />

      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-outline-secondary px-4" @click="closeConfirmModal">
          Cancel
        </button>
        <button type="button" class="btn btn-primary px-4" @click="confirmSubmit"
          :disabled="submitting || requiredErrorCount > 0">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i v-else class="fas fa-check me-1"></i>
          {{
            submitting
              ? "Submitting..."
              : requiredErrorCount > 0
                ? "Fix Required Questions"
                : "Yes, Submit"
          }}
        </button>
      </div>
    </FormModal>
  </div>
</template>

<script>
import { form_api_submit } from "@/utils/FormSubmit";
import LoadingBar from "@/components/utils/LoadingBar.vue";
import FormModal from "@/components/utils/FormModal.vue";
import AttachmentList from "@/views/WO/Schedule/Includes/AttachmentList.vue";
import { getStatusBadge } from "@/utils/getStatusBadge";

export default {
  name: "ExternalConductSchedule",
  components: {
    LoadingBar,
    FormModal,
    AttachmentList,
  },
  data() {
    return {
      detailsExpanded: false,
      putOnHold: false,
      releaseFromHold: false,
      loading: false,
      comments: [],
      newComment: "",
      commentFile: null,
      userId: localStorage.getItem("user_main_id"),
      workOrder: {
        name: "",
        external_id: "",
      },
      template: {
        get_content: [],
      },
      schedule: {},
      answers: {},
      fileNames: {},
      submitting: false,
      correctionCount: 0,
      correctiveActionCount: 0,
      requiredErrorCount: 0,
      requiredErrors: [],
      questionErrors: [],
      showErrorList: false,
      actions: {
        root_cause: "",
        actions: "",
        challenges: "",
        corrections: "",
      },
      verification: {
        remarks: "",
      },
      isSubmitted: false,
      submitResult: {},
      uploadingFiles: {},
      fileUploadProgress: {},
      isAuthorized: true,
      authMessage: "",
      showUnauthorized: false,
      // Materials data
      materialsUsed: [],
      materialsDropdown: [],
      materialRows: [],
      // Hold status
      isOnHold: false,
      actionFieldsError: false,
    };
  },
  computed: {
    isReadOnly() {
      return (
        this.submitResult?.status === "under_action" ||
        this.submitResult?.status === "under_verification"
      );
    },
  },
  mounted() {
    this.initiate();
  },
  methods: {
    getStatusBadge,
    statusClicked() {
      // Placeholder for click handler
    },

    // Materials Methods
    openMaterialsModal() {
      if (this.materialsUsed && this.materialsUsed.length > 0) {
        this.materialRows = this.materialsUsed.map((material) => ({
          material_id: material.material_id,
          quantity: material.quantity,
          id: material.id,
        }));
      } else {
        this.materialRows = [];
      }
      this.$refs.materialsModal.showModal();
    },

    closeMaterialsModal() {
      this.$refs.materialsModal.closeModal();
    },

    addMaterialRow() {
      this.materialRows.push({
        material_id: "",
        quantity: null,
      });
    },

    removeMaterialRow(index) {
      this.materialRows.splice(index, 1);
    },

    async saveMaterials() {
      for (const row of this.materialRows) {
        if (!row.material_id) {
          this.$toast("Please select a material for all rows", false);
          return;
        }
        if (!row.quantity || row.quantity <= 0) {
          this.$toast("Please enter valid quantity for all rows", false);
          return;
        }
      }

      if (this.materialRows.length === 0) {
        this.$toast("Please add at least one material", false);
        return;
      }

      this.submitting = true;

      try {
        const payload = {
          materials: this.materialRows.map((row) => ({
            material_id: row.material_id,
            quantity: row.quantity,
          })),
        };

        const result = await form_api_submit({
          url: "wo-external/schedule/external/material/save/" + this.$route.params.id,
          method: "POST",
          data: payload,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.materialsUsed = result.data.parts || [];
          this.closeMaterialsModal();
        }
      } catch (error) {
        console.error("Error saving materials:", error);
      }

      this.submitting = false;
    },

    // Attachment Methods
    removeAttachment(questionId, index) {
      if (confirm("Are you sure you want to remove this file?")) {
        const question = this.findQuestionById(questionId);
        if (question && question.get_attachments) {
          const attachment = question.get_attachments[index];
          if (attachment.id) {
            form_api_submit({
              url: "wo-external/schedule/external/delete/file/" + attachment.id,
              method: "DELETE",
              show_success_toast: false,
              show_error_toast: true,
            });
          }
          question.get_attachments.splice(index, 1);
          this.$toast("File removed", true);
        }
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "";
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
    },

    async handleAttachmentSelect(questionId, files) {
      if (!files || files.length === 0) return;

      for (const file of files) {
        if (!file.note) {
          file.note = "";
        }
        await this.uploadFile(questionId, file);
      }
    },

    async uploadFile(questionId, file) {
      if (!file) return;

      this.uploadingFiles[questionId] = true;

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("question_id", questionId);
        formData.append("schedule_id", this.schedule.id);

        if (file.note) {
          formData.append("note", file.note);
        }

        const result = await form_api_submit({
          url: "wo-external/schedule/external/upload/file",
          method: "POST",
          data: formData,
          show_success_toast: false,
          show_error_toast: true,
        });

        if (result.success) {
          const uploadedFile = result.data.file;

          const question = this.findQuestionById(questionId);
          if (question) {
            if (!question.get_attachments) {
              question.get_attachments = [];
            }
            question.get_attachments.push({
              id: uploadedFile.id,
              file_name: uploadedFile.file_name || file.name,
              file_path: uploadedFile.file_path || uploadedFile.url,
              file_type: uploadedFile.file_type || file.type,
              file_size: uploadedFile.file_size || file.size,
              note: file.note || "",
            });
          }

          this.$toast("File uploaded successfully!", true);
        }
      } catch (error) {
        console.error("Upload error:", error);
        this.$toast("Failed to upload file. Please try again.", false);
      } finally {
        this.uploadingFiles[questionId] = false;
      }
    },

    findQuestionById(questionId) {
      const search = (items) => {
        if (!items) return null;
        for (const item of items) {
          if (item.id === questionId) return item;
          if (item.get_content && item.get_content.length > 0) {
            const found = search(item.get_content);
            if (found) return found;
          }
        }
        return null;
      };
      return search(this.template.get_content);
    },

    getAttachments(question) {
      return question?.get_attachments || [];
    },

    async updateAttachmentNote(questionId, fileId, note) {
      const question = this.findQuestionById(questionId);
      if (question && question.get_attachments) {
        const file = question.get_attachments.find((f) => f.id === fileId);
        if (file) {
          file.note = note;
        }
      }

      try {
        const result = await form_api_submit({
          url: "wo-external/schedule/external/update-note/" + fileId,
          method: "POST",
          data: { note: note },
          show_success_toast: false,
          show_error_toast: true,
        });

        if (result.success) {
          this.$toast("Note updated successfully!", true);
        }
      } catch (error) {
        console.error("Failed to update note:", error);
        this.$toast("Failed to update note. Please try again.", false);
      }
    },

    updateAnswer(question, value) {
      if (!this.answers[question.id]) {
        this.answers[question.id] = {};
      }
      this.answers[question.id].answer = value;

      if (question.get_response?.type === "multiple_choice") {
        this.checkLogics(question);
      }
    },

    updateNote(question, value) {
      if (!this.answers[question.id]) {
        this.answers[question.id] = {};
      }
      this.answers[question.id].notes_description = value;
    },

    checkLogics(question) {
      const selectedOption = this.getAnswer(question);

      if (this.answers[question.id]) {
        delete this.answers[question.id].correction;
        delete this.answers[question.id].corrective_action;
        delete this.answers[question.id].notes_description;
      }

      if (!question.get_logics || question.get_logics.length === 0) return;

      const selectedOptions = Array.isArray(selectedOption)
        ? selectedOption
        : [selectedOption];

      for (const logic of question.get_logics) {
        const logicAnswers = logic.get_answers?.map((a) => a.id) || [];

        let isSelected = false;
        for (const opt of selectedOptions) {
          if (logicAnswers.includes(opt)) {
            isSelected = true;
            break;
          }
        }

        let conditionMet = false;
        if (logic.condition === "is_one_of") {
          conditionMet = isSelected;
        } else if (logic.condition === "is_none_of") {
          conditionMet = !isSelected;
        }

        if (conditionMet) {
          if (!this.answers[question.id]) {
            this.answers[question.id] = {};
          }

          if (logic.trigger === "correction") {
            this.answers[question.id].correction = true;
          } else if (
            logic.trigger === "corrective_action" ||
            logic.trigger === "take_corrective_action"
          ) {
            this.answers[question.id].corrective_action = true;
          } else if (logic.trigger === "notes") {
            this.answers[question.id].notes_description = "";
          }
        }
      }
    },

    getCorrection(question) {
      if (
        this.answers[question.id] &&
        this.answers[question.id].correction !== undefined
      ) {
        return this.answers[question.id].correction;
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.correction || false;
      }
      return false;
    },

    getCorrectiveAction(question) {
      if (
        this.answers[question.id] &&
        this.answers[question.id].corrective_action !== undefined
      ) {
        return this.answers[question.id].corrective_action;
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.corrective_action || false;
      }
      return false;
    },

    getNoteTrigger(question) {
      if (
        this.answers[question.id] &&
        this.answers[question.id].notes_description !== undefined
      ) {
        return true;
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.notes || false;
      }
      return false;
    },

    getNoteValue(question) {
      if (
        this.answers[question.id] &&
        this.answers[question.id].notes_description !== undefined
      ) {
        return this.answers[question.id].notes_description;
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.notes_description || "";
      }
      return "";
    },

    getAnswer(question) {
      if (
        this.answers[question.id] &&
        this.answers[question.id].answer !== undefined
      ) {
        return this.answers[question.id].answer;
      }

      if (question.get_schedule_answer) {
        const scheduleAnswer = question.get_schedule_answer;

        if (
          scheduleAnswer.get_attachments &&
          scheduleAnswer.get_attachments.length > 0
        ) {
          if (!this.answers[question.id]) {
            this.answers[question.id] = {};
          }
          this.answers[question.id].attachments =
            scheduleAnswer.get_attachments.map((att) => ({
              id: att.id,
              file_name: att.file_name,
              file_path: att.file_path,
              file_type: att.file_type,
              file_size: att.file_size,
              note: att.note || "",
            }));
        }

        if (
          question.get_response &&
          question.get_response.type === "multiple_choice"
        ) {
          if (
            scheduleAnswer.get_answer_options &&
            scheduleAnswer.get_answer_options.length > 0
          ) {
            if (
              !question.multi_select &&
              scheduleAnswer.get_answer_options.length === 1
            ) {
              return scheduleAnswer.get_answer_options[0].option_id;
            }
            if (question.multi_select) {
              return scheduleAnswer.get_answer_options.map(
                (opt) => opt.option_id,
              );
            }
          }
          return null;
        }
        if (
          scheduleAnswer.answer !== null &&
          scheduleAnswer.answer !== undefined
        ) {
          return scheduleAnswer.answer;
        }
      }
      return "";
    },

    isSelected(question, option) {
      const selected = this.getAnswer(question);
      if (question.multi_select) {
        return Array.isArray(selected) && selected.includes(option.id);
      } else {
        return selected === option.id;
      }
    },

    selectOption(question, option) {
      if (question.multi_select) {
        let selected = this.getAnswer(question);
        if (!Array.isArray(selected)) {
          selected = [];
        }

        const index = selected.indexOf(option.id);
        if (index > -1) {
          selected.splice(index, 1);
        } else {
          selected.push(option.id);
        }

        if (selected.length === 0) {
          delete this.answers[question.id];
        } else {
          if (!this.answers[question.id]) {
            this.answers[question.id] = {};
          }
          this.answers[question.id].answer = selected;
        }
      } else {
        if (this.isSelected(question, option)) {
          delete this.answers[question.id];
        } else {
          if (!this.answers[question.id]) {
            this.answers[question.id] = {};
          }
          this.answers[question.id].answer = option.id;
        }
      }

      this.checkLogics(question);
    },

    handleFileUpload(questionId, event) {
      const file = event.target.files[0];
      if (file) {
        this.fileNames[questionId] = file.name;
        if (!this.answers[questionId]) {
          this.answers[questionId] = {};
        }
        this.answers[questionId].answer = file;
      }
    },

    hasQuestionError(question) {
      return this.questionErrors.some(
        (error) => error.questionId === question.id,
      );
    },

    clearQuestionErrors() {
      this.questionErrors = [];
    },

    scrollToFirstQuestionError() {
      const firstError = this.questionErrors[0];
      if (!firstError) return;

      this.$nextTick(() => {
        const target = document.querySelector(
          `[data-question-id="${firstError.questionId}"]`,
        );
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    closeConfirmModal() {
      this.$refs.confirmModal.closeModal();
    },

    async openConfirmModal() {
      this.submitting = true;
      this.showErrorList = false;
      this.clearQuestionErrors();
      this.actionFieldsError = false;

      const counts = this.countCorrectionsAndActions();
      this.correctionCount = counts.corrections;
      this.correctiveActionCount = counts.correctiveActions;

      const validationErrors = this.validateData();
      this.requiredErrorCount = validationErrors.length;
      this.requiredErrors = validationErrors;
      this.questionErrors = validationErrors;

      this.$refs.confirmModal.showModal();
      this.submitting = false;
      this.scrollToFirstQuestionError();
    },

    validateActionFields() {
      // Only validate if actions are required (correction or corrective action exists)
      if (this.correctionCount > 0 || this.correctiveActionCount > 0) {
        if (!this.actions.root_cause || !this.actions.root_cause.trim()) {
          this.actionFieldsError = true;
          this.$toast("Please enter Diagnosis/Root Causes", false);
          return false;
        }
        if (!this.actions.actions || !this.actions.actions.trim()) {
          this.actionFieldsError = true;
          this.$toast("Please enter Actions Taken/Performed", false);
          return false;
        }
      }
      this.actionFieldsError = false;
      return true;
    },

    confirmSubmit() {
      // Validate action fields if needed
      if (this.correctionCount > 0 || this.correctiveActionCount > 0) {
        if (!this.validateActionFields()) {
          return;
        }
      }
      this.$refs.confirmModal.closeModal();
      this.handleSubmit();
    },

    async saveVerificationRemarks() {
      if (!this.verification.remarks) {
        this.$toast("Please enter verification remarks", false);
        return;
      }

      this.submitting = true;

      try {
        const payload = {
          remarks: this.verification.remarks,
        };

        const result = await form_api_submit({
          url: "wo-external/schedule/external/conduct/save/" + this.$route.params.id,
          method: "POST",
          data: payload,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.isSubmitted = true;
          this.submitResult = result.data;
        }
      } catch (error) {
        console.error("Submission error:", error);
      }

      this.submitting = false;
    },

    async saveActions() {
      this.submitting = true;

      try {
        const result = await form_api_submit({
          url: "wo-external/schedule/external/conduct/save/" + this.$route.params.id,
          method: "POST",
          data: this.actions,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.isSubmitted = true;
          this.submitResult = result.data;
        }
      } catch (error) {
        console.error("Submission error:", error);
      }

      this.submitting = false;
    },

    async saveDirectActions() {
      this.submitting = true;
      var payload = {
        root_cause: this.actions.root_cause,
        actions: this.actions.actions,
        challenges: this.actions.challenges,
        is_direct: true,
      };

      try {
        const result = await form_api_submit({
          url: "wo-external/schedule/external/conduct/save/" + this.$route.params.id,
          method: "POST",
          data: payload,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.isSubmitted = true;
          this.submitResult = result.data;
        }
      } catch (error) {
        console.error("Submission error:", error);
      }

      this.submitting = false;
    },

    async handleSubmit() {
      this.submitting = true;

      try {
        const payload = {
          responses: this.answers,
          root_cause: this.actions.root_cause || null,
          actions: this.actions.actions || null,
          challenges: this.actions.challenges || null,
        };

        const result = await form_api_submit({
          url: "wo-external/schedule/external/conduct/save/" + this.$route.params.id,
          method: "POST",
          data: payload,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.isSubmitted = true;
          this.submitResult = result.data;
        }
      } catch (error) {
        console.error("Submission error:", error);
      }

      this.submitting = false;
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getFileName(path) {
      if (!path) return "";
      return path.split("/").pop();
    },

    getFilePath(path) {
      return this.$backend_domain + path;
    },

    handleCommentFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.commentFile = file;
      }
      event.target.value = "";
    },

    removeCommentFile() {
      this.commentFile = null;
    },

    async saveComment() {
      if (!this.newComment.trim()) {
        this.$toast("Please enter a comment", false);
        return;
      }

      this.submitting = true;

      try {
        const formData = new FormData();
        formData.append("note", this.newComment);
        formData.append("schedule_id", this.$route.params.id);

        if (this.putOnHold) {
          formData.append("status", "on_hold");
        }

        if (this.releaseFromHold) {
          formData.append("status", "release");
        }

        if (this.commentFile) {
          formData.append("attachment", this.commentFile);
        }

        const result = await form_api_submit({
          url: "wo-external/schedule/external/comment/save",
          method: "POST",
          data: formData,
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.comments.unshift(result.data.comment);
          this.newComment = "";
          this.commentFile = null;
          this.putOnHold = false;
          this.releaseFromHold = false;

          if (result.data.schedule) {
            this.schedule.status = result.data.schedule.status;
            this.schedule.backup_status = result.data.schedule.backup_status;
          }
        }
      } catch (error) {
        console.error("Error posting comment:", error);
      }

      this.submitting = false;
    },

    async deleteComment(commentId) {
      if (!confirm("Are you sure you want to delete this comment?")) return;

      try {
        const result = await form_api_submit({
          url: "wo-external/schedule/external/comment/delete/" + commentId,
          method: "DELETE",
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          this.comments = this.comments.filter((c) => c.id !== commentId);
        }
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    },

    async deleteAttachment(commentId) {
      if (!confirm("Are you sure you want to remove this attachment?")) return;

      try {
        const result = await form_api_submit({
          url: "wo-external/schedule/external/comment/attachment/delete/" + commentId,
          method: "DELETE",
          show_success_toast: true,
          show_error_toast: true,
        });

        if (result.success) {
          const comment = this.comments.find((c) => c.id === commentId);
          if (comment) {
            comment.attachment = null;
          }
        }
      } catch (error) {
        console.error("Error deleting attachment:", error);
      }
    },

    async initiate() {
      this.loading = true;

      const result = await form_api_submit({
        url: "wo-external/schedule/external/conduct/" + this.$route.params.id,
        method: "POST",
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false,
      });

      if (result.success) {
        this.workOrder = result.data.work_order || {};
        this.schedule = result.data.schedule || {};
        this.comments = this.schedule.get_comments || [];
        this.submitResult = result.data.schedule || {};
        this.template = result.data.template || { get_content: [] };

        this.materialsUsed = this.schedule.get_materials || [];
        this.materialsDropdown = result.data.materials || [];

        this.isOnHold = this.schedule.status === "on_hold";

        if (this.schedule.get_action) {
          this.actions.root_cause = this.schedule.get_action.root_cause || "";
          this.actions.actions = this.schedule.get_action.actions || "";
          this.actions.challenges = this.schedule.get_action.challenges || "";
        }

        if (this.schedule.get_verification) {
          this.verification.remarks =
            this.schedule.get_verification.verification || "";
        }

        this.isAuthorized = true;
        this.showUnauthorized = false;
      }

      this.loading = false;
    },

    countCorrectionsAndActions() {
      let corrections = 0;
      let correctiveActions = 0;

      const checkQuestions = (questions) => {
        if (!questions) return;

        for (const question of questions) {
          if (question.type === "question") {
            let hasCorrection = false;
            let hasCorrectiveAction = false;

            if (this.answers[question.id]) {
              if (this.answers[question.id].correction === true) {
                hasCorrection = true;
              }
              if (this.answers[question.id].corrective_action === true) {
                hasCorrectiveAction = true;
              }
            }

            if (!hasCorrection && !hasCorrectiveAction) {
              if (question.get_schedule_answer) {
                if (question.get_schedule_answer.correction === true) {
                  hasCorrection = true;
                }
                if (question.get_schedule_answer.corrective_action === true) {
                  hasCorrectiveAction = true;
                }
              }
            }

            if (hasCorrection) corrections++;
            if (hasCorrectiveAction) correctiveActions++;
          }

          if (question.get_content && question.get_content.length > 0) {
            checkQuestions(question.get_content);
          }
        }
      };

      checkQuestions(this.template.get_content);
      return { corrections, correctiveActions };
    },

    validateData() {
      let errors = [];

      const checkQuestions = (questions) => {
        if (!questions) return;

        for (const question of questions) {
          if (question.type === "question" && question.required === true) {
            let hasAnswer = false;

            if (
              this.answers[question.id] &&
              this.answers[question.id].answer !== undefined
            ) {
              const answer = this.answers[question.id].answer;
              if (answer !== null && answer !== undefined && answer !== "") {
                if (Array.isArray(answer)) {
                  if (answer.length > 0) {
                    hasAnswer = true;
                  }
                } else {
                  hasAnswer = true;
                }
              }
            }

            if (!hasAnswer && !this.answers[question.id]) {
              if (question.get_schedule_answer) {
                const savedAnswer = question.get_schedule_answer.answer;
                if (
                  savedAnswer !== null &&
                  savedAnswer !== undefined &&
                  savedAnswer !== ""
                ) {
                  hasAnswer = true;
                }
              }
            }

            if (!hasAnswer) {
              errors.push({
                questionId: question.id,
                title: question.title || "Untitled Question",
              });
            }
          }

          if (question.get_content && question.get_content.length > 0) {
            checkQuestions(question.get_content);
          }
        }
      };

      checkQuestions(this.template.get_content);
      return errors;
    },

    toggleDetails() {
      this.detailsExpanded = !this.detailsExpanded;
    },

    getPriorityClass(priority) {
      const classes = {
        'low': 'priority-low',
        'medium': 'priority-medium',
        'high': 'priority-high'
      };
      return classes[priority] || '';
    },

    isImageFile(fileName) {
      if (!fileName) return false;
      const extensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
      const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
      return extensions.includes(ext);
    },

    previewImage(filePath) {
      // Open image in new window
      const fullPath = this.getFilePath(filePath);
      window.open(fullPath, '_blank');
    },
  },
};
</script>

<style scoped>
/* Unauthorized Styles */
.unauthorized-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.unauthorized-card {
  background: white;
  border-radius: 16px;
  padding: 40px 50px;
  text-align: center;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #fee2e2;
}

.unauthorized-icon {
  width: 80px;
  height: 80px;
  background: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.unauthorized-icon i {
  font-size: 40px;
  color: #dc2626;
}

.unauthorized-title {
  font-size: 22px;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 8px;
}

.unauthorized-message {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
}

/* Status Badge Styles */
.badge-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.badge-pending {
  background: #fef3c7;
  color: #92400e;
}

.badge-progress {
  background: #dbeafe;
  color: #1e40af;
}

.badge-action {
  background: #fef3c7;
  color: #92400e;
}

.badge-verification {
  background: #dbeafe;
  color: #1e40af;
}

.badge-completed {
  background: #d1fae5;
  color: #065f46;
}

.badge-rejected {
  background: #fee2e2;
  color: #991b1b;
}

.badge-default {
  background: #f1f5f9;
  color: #475569;
}

.badge-status i {
  font-size: 12px;
}

/* Attachment Styles */
.attachment-wrapper {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e9ecef;
}

.attachment-link {
  color: #667eea;
  text-decoration: none;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-size {
  color: #999;
  font-size: 10px;
}

/* Rest of existing styles... */
.conduct-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.form-header {
  background: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

.form-header h3 {
  color: #1a202c;
}

/* Heading Block */
.heading-block {
  margin: 28px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
}

.heading-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

/* Section Block */
.section-block {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 20px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.section-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e9ecef;
}

.section-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e9edf2 100%);
}

.section-icon {
  color: #667eea;
  font-size: 18px;
  background: rgba(102, 126, 234, 0.1);
  padding: 6px;
  border-radius: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
}

.sub-questions {
  padding: 16px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sub-question {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 0;
  transition: all 0.2s;
}

.sub-question:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.sub-question:last-child {
  margin-bottom: 0;
}

/* Question Block */
.question-block {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.question-block:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Logic Messages */
.logic-message {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.logic-message.correction {
  background: #fef3c7;
  color: #d97706;
  border-left: 3px solid #d97706;
}

.logic-message.corrective-action {
  background: #fee2e2;
  color: #dc2626;
  border-left: 3px solid #dc2626;
}

.has-correction {
  border-left: 3px solid #d97706;
}

.has-corrective-action {
  border-left: 3px solid #dc2626;
}

.has-error {
  border: 1px solid #f56565 !important;
  box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.08);
}

.question-error-text {
  margin-top: 10px;
  color: #c53030;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.question-error-text i {
  color: #e53e3e;
}

.note-textarea-wrapper {
  margin-top: 12px;
}

/* Question Header */
.question-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.question-number {
  font-weight: 600;
  color: #667eea;
  font-size: 14px;
}

.question-text {
  font-size: 15px;
  font-weight: 500;
  color: #1a202c;
  flex: 1;
}

.required-badge {
  color: #ef4444;
  font-weight: 700;
  font-size: 18px;
}

.multi-badge {
  background: #e0e7ff;
  color: #4338ca;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 12px;
  text-transform: uppercase;
}

/* Options Grid */
.options-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.option-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
  flex: 0 1 auto;
}

.option-box:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.option-color {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
}

.option-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a202c;
}

.option-check {
  margin-left: auto;
}

.option-check i {
  font-size: 18px;
}

.text-muted {
  color: #6c757d;
  font-size: 12px;
  margin-top: 6px;
  display: block;
}

/* Input Wrappers */
.text-input-wrapper textarea,
.text-input-wrapper input {
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;
}

.text-input-wrapper textarea:focus,
.text-input-wrapper input:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.number-input-wrapper input {
  width: 200px;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  background: white;
}

.number-input-wrapper input:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.datetime-input-wrapper input,
.date-input-wrapper input,
.time-input-wrapper input {
  width: 220px;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  background: white;
}

.datetime-input-wrapper input:focus,
.date-input-wrapper input:focus,
.time-input-wrapper input:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* File Input */
.file-input-wrapper {
  width: 100%;
}

.file-input-wrapper input[type="file"] {
  padding: 10px 14px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  width: 100%;
  font-size: 13px;
  background: white;
}

.file-input-wrapper input[type="file"]:focus {
  border-color: #667eea;
  outline: none;
}

.file-name {
  margin-top: 8px;
  font-size: 13px;
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Range Input */
.range-input-wrapper {
  width: 100%;
  padding: 8px 0;
}

.range-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.range-value {
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
}

.range-label {
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.form-range {
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  -webkit-appearance: none;
  appearance: none;
}

.form-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #667eea;
  border-radius: 50%;
  cursor: pointer;
}

.form-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #667eea;
  border-radius: 50%;
  cursor: pointer;
}

.range-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

/* Form Actions */
.form-actions {
  margin-top: 30px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.form-actions .btn {
  padding: 10px 32px;
  font-size: 15px;
  border-radius: 8px;
}

/* Table Block */
.table-block {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-icon {
  color: #fa709a;
  font-size: 18px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

/* Success Container */
.success-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.success-card {
  background: white;
  border-radius: 16px;
  padding: 40px 50px;
  text-align: center;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.success-icon i {
  font-size: 40px;
  color: #10b981;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
}

.success-message {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 24px;
}

.status-message {
  margin-top: 16px;
}

.status-box {
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.status-box i {
  font-size: 18px;
  flex-shrink: 0;
}

.status-action {
  background: #fef3c7;
  color: #92400e;
  border-left: 4px solid #f59e0b;
}

.status-action i {
  color: #f59e0b;
}

.status-verification {
  background: #dbeafe;
  color: #1e40af;
  border-left: 4px solid #3b82f6;
}

.status-verification i {
  color: #3b82f6;
}

.status-completed {
  background: #d1fae5;
  color: #065f46;
  border-left: 4px solid #10b981;
}

.status-completed i {
  color: #10b981;
}

.status-default {
  background: #f1f5f9;
  color: #475569;
  border-left: 4px solid #94a3b8;
}

.status-default i {
  color: #64748b;
}

/* Action Section */
.action-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.action-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e9ecef;
}

.action-header i {
  color: #667eea;
  font-size: 18px;
  background: rgba(102, 126, 234, 0.1);
  padding: 6px;
  border-radius: 8px;
}

.action-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
}

.action-body {
  padding: 20px;
}

.action-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;
}

.action-textarea:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.action-textarea::placeholder {
  color: #94a3b8;
}

/* Materials Section */
.materials-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.materials-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
}

.materials-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.materials-header i {
  color: #667eea;
  font-size: 18px;
  background: rgba(102, 126, 234, 0.1);
  padding: 6px;
  border-radius: 8px;
}

.materials-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
}

.btn-edit-materials {
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  color: #667eea;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-edit-materials:hover:not(:disabled) {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-edit-materials:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.materials-list {
  padding: 12px 20px;
}

.material-item {
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.material-item:last-child {
  border-bottom: none;
}

.material-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.material-name {
  font-weight: 500;
  color: #1a202c;
  font-size: 14px;
}

.material-quantity {
  color: #667eea;
  font-weight: 600;
  font-size: 14px;
}

.no-materials {
  padding: 20px;
  text-align: center;
}

.no-materials p {
  margin: 0;
  color: #94a3b8;
  font-style: italic;
  font-size: 14px;
}

/* Materials Modal */
.materials-modal-body {
  padding: 20px 0;
}

.material-row {
  background: #f8fafc;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.2s;
}

.material-row:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.material-row .form-label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.material-row .form-control-thin,
.material-row .form-select {
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 8px;
}

.btn-remove-row {
  background: transparent;
  border: none;
  color: #dc2626;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.btn-remove-row:hover {
  background: #fee2e2;
}

.modal-footer-actions {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

/* Comments Section */
.comments-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.comments-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e9ecef;
}

.comments-header i {
  color: #667eea;
  font-size: 18px;
  background: rgba(102, 126, 234, 0.1);
  padding: 6px;
  border-radius: 8px;
}

.comments-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
}

.comments-header .ms-auto {
  margin-left: auto;
}

/* Checkbox Button Styles */
.checkbox-button-wrapper {
  display: inline-block;
}

.checkbox-button {
  position: relative;
  cursor: pointer;
}

.checkbox-button input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.checkbox-btn i {
  font-size: 12px;
}

.checkbox-button input:checked+.checkbox-btn {
  background: #fef3c7;
  color: #92400e;
  border-color: #f59e0b;
}

.checkbox-btn.release-btn {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

.checkbox-button input:checked+.release-btn {
  background: #d1fae5;
  color: #065f46;
  border-color: #10b981;
}

.checkbox-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.checkbox-button input:checked+.checkbox-btn:hover {
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.checkbox-button input:checked+.release-btn:hover {
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

/* Badge styles for comments */
.badge-hold {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fef3c7;
  color: #92400e;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.badge-release {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #d1fae5;
  color: #065f46;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.comments-list {
  padding: 16px 20px;
  max-height: 500px;
  overflow-y: auto;
}

.comment-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 16px 0;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.comment-user {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.comment-user i {
  font-size: 20px;
  color: #667eea;
}

.comment-username {
  font-weight: 600;
  color: #1a202c;
  font-size: 14px;
}

.comment-date {
  font-size: 12px;
  color: #94a3b8;
}

.comment-body {
  padding-left: 32px;
}

.comment-text {
  font-size: 14px;
  color: #334155;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.no-comments {
  padding: 30px 20px;
  text-align: center;
}

.no-comments p {
  margin: 0;
  color: #94a3b8;
  font-style: italic;
}

/* Comment Form */
.comment-form {
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #e9ecef;
  background: #fafbfc;
}

.comment-input-wrapper textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;
  resize: vertical;
}

.comment-input-wrapper textarea:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.comment-attachment-upload {
  margin-top: 10px;
}

.comment-attachment-upload input[type="file"] {
  padding: 8px 14px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  width: 100%;
  font-size: 13px;
  background: white;
}

.comment-attachment-upload input[type="file"]:focus {
  border-color: #667eea;
  outline: none;
}

.comment-file-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e0e7ff;
  color: #4338ca;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-top: 8px;
}

.comment-file-item i {
  font-size: 12px;
}

.comment-file-item .btn-close {
  width: 14px;
  height: 14px;
  font-size: 10px;
  padding: 0;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.comment-actions .btn {
  padding: 8px 20px;
  font-size: 13px;
  border-radius: 8px;
}

.comment-actions .btn-link {
  padding: 0 4px;
  font-size: 12px;
}

.comment-attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.comment-attachment i {
  color: #667eea;
}

.comment-attachment .attachment-link {
  color: #667eea;
  text-decoration: none;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-attachment .attachment-link:hover {
  text-decoration: underline;
}

/* Modal Action Fields */
.action-fields-modal {
  padding: 8px 0;
}

.action-fields-modal .form-label-thin {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.action-fields-modal .action-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  transition: border-color 0.2s;
  background: white;
  resize: vertical;
}

.action-fields-modal .action-textarea:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Responsive */
@media (max-width: 640px) {
  .conduct-form {
    padding: 12px;
  }

  .question-block,
  .sub-question {
    padding: 16px;
  }

  .options-grid {
    gap: 8px;
  }

  .option-box {
    flex: 1 1 100%;
    justify-content: center;
  }

  .number-input-wrapper input,
  .datetime-input-wrapper input,
  .date-input-wrapper input,
  .time-input-wrapper input {
    width: 100%;
  }

  .section-block {
    padding: 0;
  }

  .section-header {
    padding: 12px 16px;
  }

  .sub-questions {
    padding: 12px 16px 16px 16px;
    gap: 12px;
  }

  .attachment-item {
    font-size: 11px;
    padding: 3px 8px;
  }

  .attachment-link {
    max-width: 100px;
  }

  .comment-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .comment-body {
    padding-left: 0;
  }

  .comment-attachment-item .attachment-link {
    max-width: 100px;
  }

  .material-row .row>div {
    margin-bottom: 8px;
  }

  .materials-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .btn-edit-materials {
    font-size: 11px;
    padding: 3px 10px;
  }

  .modal-footer-actions {
    flex-wrap: wrap;
  }

  .modal-footer-actions .btn {
    width: 100%;
  }

  .comments-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .action-fields-modal .action-textarea {
    font-size: 12px;
    padding: 6px 10px;
  }
}

/* Work Order Details Section */
/* Form Header with Details */
.form-header {
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.form-header h3 {
  color: #1a202c;
}

.toggle-details-btn {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 6px;
  border-color: #e0e0e0;
  color: #64748b;
  background: transparent;
  transition: all 0.2s;
}

.toggle-details-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.toggle-details-btn i {
  transition: transform 0.3s ease;
  font-size: 12px;
}

.toggle-details-btn i.rotated {
  transform: rotate(180deg);
}

.details-section {
  padding: 4px 0 0 0;
}

.details-section hr {
  border-color: #e9ecef;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0;
}

.detail-label {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

/* Priority Badges */
.priority-badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.priority-low {
  background: #dbeafe;
  color: #1e40af;
}

.priority-medium {
  background: #fef3c7;
  color: #92400e;
}

.priority-high {
  background: #fee2e2;
  color: #991b1b;
}

/* Badge Tags */
.badge-tag {
  display: inline-block;
  background: #f1f5f9;
  color: #334155;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  margin: 2px 4px 2px 0;
}

.badge-tag:last-child {
  margin-right: 0;
}

/* Attachments List */
.attachments-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.attachments-list .attachment-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f8f9fa;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e9ecef;
}

.attachments-list .attachment-link {
  color: #667eea;
  text-decoration: none;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachments-list .attachment-link:hover {
  text-decoration: underline;
}

/* Image Thumbnail */
.image-thumbnail {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  flex-shrink: 0;
}

.thumbnail-img {
  max-width: 80px;
  max-height: 60px;
  object-fit: cover;
  transition: transform 0.2s;
}

.thumbnail-img:hover {
  transform: scale(1.05);
}

/* Responsive */
@media (max-width: 640px) {
  .form-header {
    padding: 12px;
  }

  .toggle-details-btn {
    font-size: 10px;
    padding: 3px 8px;
  }

  .toggle-details-btn span {
    display: none;
  }

  .detail-item {
    margin-bottom: 6px;
  }

  .badge-tag {
    font-size: 10px;
    padding: 1px 8px;
  }

  .attachments-list .attachment-item {
    font-size: 10px;
    padding: 3px 8px;
  }

  .attachments-list .attachment-link {
    max-width: 100px;
  }

  .thumbnail-img {
    max-width: 60px;
    max-height: 45px;
  }
}
</style>